import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
    InputLabel, Card, CardContent, Chip, Alert, CircularProgress, InputAdornment,
    Popover, List, ListItem, ListItemText, ListItemIcon, Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    getAportesResgates, addAporteResgate, updateAporteResgate, deleteAporteResgate,
    getObras, getInvestidores, getAporteResgateAnexoUrl,
    Obra, Investidor
} from '../services/api';
import { AporteResgate } from '../services/types';
import { supabase } from '../services/supabaseClient';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
};

const AportesResgatesPage: React.FC = () => {
    const [aportesResgates, setAportesResgates] = useState<AporteResgate[]>([]);
    const [obras, setObras] = useState<Obra[]>([]);
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters
    const [selectedObra, setSelectedObra] = useState<number | ''>('');
    const [selectedInvestidor, setSelectedInvestidor] = useState<number | ''>('');
    const [filterDataInicio, setFilterDataInicio] = useState<string>('');
    const [filterDataFim, setFilterDataFim] = useState<string>('');

    // Modal
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState<AporteResgate | null>(null);

    // Attachments View Popover
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [viewingAnexos, setViewingAnexos] = useState<{ id: number; caminho_arquivo: string; nome_original: string }[]>([]);

    const handleOpenAnexos = (event: React.MouseEvent<HTMLButtonElement>, anexos: any[]) => {
        setAnchorEl(event.currentTarget);
        setViewingAnexos(anexos);
    };

    const handleCloseAnexos = () => {
        setAnchorEl(null);
        setViewingAnexos([]);
    };

    const openPopover = Boolean(anchorEl);
    const idPopover = openPopover ? 'simple-popover' : undefined;

    const [formData, setFormData] = useState<Partial<AporteResgate>>({
        tipo: 'Aporte',
        data: new Date().toISOString().split('T')[0],
        valor: '' as any
    });

    // Attachments State
    const [anexos, setAnexos] = useState<File[]>([]);
    const [existingAnexos, setExistingAnexos] = useState<{ id: number; caminho_arquivo: string; nome_original: string }[]>([]);
    const [anexosToDelete, setAnexosToDelete] = useState<{ id: number; caminho_arquivo: string }[]>([]);
    const [uploading, setUploading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [data, obrasData, investidoresData] = await Promise.all([
                getAportesResgates({ 
                    obra_id: Number(selectedObra) || undefined, 
                    investidor_id: Number(selectedInvestidor) || undefined,
                    data_inicio: filterDataInicio || undefined,
                    data_fim: filterDataFim || undefined
                }),
                getObras(),
                getInvestidores()
            ]);
            setAportesResgates(data || []);
            setObras(obrasData || []);
            setInvestidores(investidoresData || []);
        } catch (err) {
            console.error(err);
            setError('Erro ao carregar dados.');
        } finally {
            setLoading(false);
        }
    }, [selectedObra, selectedInvestidor, filterDataInicio, filterDataFim]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenModal = (item?: AporteResgate) => {
        if (item) {
            setEditingItem(item);
            setFormData({ ...item, valor: item.valor === 0 ? ('' as any) : item.valor });
            setExistingAnexos(item.anexos || []);
            setAnexos([]);
            setAnexosToDelete([]);
        } else {
            setEditingItem(null);
            setFormData({
                tipo: 'Aporte',
                data: new Date().toISOString().split('T')[0],
                valor: '' as any,
                obra_id: selectedObra || undefined,
                investidor_id: selectedInvestidor || undefined
            });
            setExistingAnexos([]);
            setAnexos([]);
            setAnexosToDelete([]);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingItem(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setAnexos([...anexos, ...Array.from(event.target.files)]);
        }
    };

    const handleRemoveAnexo = (index: number) => {
        setAnexos(anexos.filter((_, i) => i !== index));
    };

    const handleRemoveExistingAnexo = (id: number) => {
        const anexoToRemove = existingAnexos.find(a => a.id === id);
        if (anexoToRemove) {
            setExistingAnexos(existingAnexos.filter(a => a.id !== id));
            setAnexosToDelete([...anexosToDelete, anexoToRemove]);
        }
    };

    const getFileIcon = (fileName: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return <ImageIcon />;
        if (ext === 'pdf') return <PictureAsPdfIcon />;
        return <InsertDriveFileIcon />;
    };

    const handleSave = async () => {
        try {
            if (!formData.obra_id || !formData.investidor_id || !formData.valor || !formData.data) {
                alert('Preencha todos os campos obrigatórios.');
                return;
            }

            setUploading(true);

            // Upload new files
            const uploadedAnexos = [];
            for (const file of anexos) {
                const fileExt = file.name.split('.').pop();
                const fileName = `aportes_resgates/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                    .from('anexos')
                    .upload(fileName, file);

                if (uploadError) {
                    console.error('Error uploading file:', uploadError);
                    alert(`Erro ao fazer upload do arquivo ${file.name}`);
                    setUploading(false);
                    return;
                }

                uploadedAnexos.push({
                    caminho_arquivo: fileName,
                    nome_original: file.name,
                    tipo_arquivo: file.type
                });
            }

            const payload = {
                obra_id: Number(formData.obra_id),
                investidor_id: Number(formData.investidor_id),
                tipo: formData.tipo as 'Aporte' | 'Resgate',
                valor: Number(formData.valor),
                data: formData.data,
                observacoes: formData.observacoes
            };

            if (editingItem) {
                // Delete removed attachments from storage
                if (anexosToDelete.length > 0) {
                    const pathsToDelete = anexosToDelete.map(a => a.caminho_arquivo);
                    const { error: deleteError } = await supabase.storage
                        .from('anexos')
                        .remove(pathsToDelete);

                    if (deleteError) {
                        console.error('Error deleting files from storage:', deleteError);
                        // We continue even if storage deletion fails, to ensure DB consistency
                    }
                }

                await updateAporteResgate(editingItem.id, payload, uploadedAnexos, anexosToDelete.map(a => a.id));
            } else {
                await addAporteResgate(payload, uploadedAnexos);
            }
            fetchData();
            handleCloseModal();
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert('Erro ao salvar.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir este registro?')) {
            try {
                const itemToDelete = aportesResgates.find(a => a.id === id);
                if (itemToDelete && itemToDelete.anexos && itemToDelete.anexos.length > 0) {
                    const paths = itemToDelete.anexos.map(a => a.caminho_arquivo);
                    const { error } = await supabase.storage.from('anexos').remove(paths);
                    if (error) console.error('Error deleting files from storage:', error);
                }

                await deleteAporteResgate(id);
                fetchData();
            } catch (err) {
                console.error(err);
                alert('Erro ao excluir.');
            }
        }
    };

    // Summary Calculations
    const totalAportes = aportesResgates.filter(i => i.tipo === 'Aporte').reduce((acc, curr) => acc + curr.valor, 0);
    const totalResgates = aportesResgates.filter(i => i.tipo === 'Resgate').reduce((acc, curr) => acc + curr.valor, 0);
    const saldo = totalAportes - totalResgates;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    Aportes e Resgates
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                    Novo Lançamento
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, alignItems: 'center' }}>
                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filtrar por Obra</InputLabel>
                            <Select
                                value={selectedObra}
                                label="Filtrar por Obra"
                                onChange={(e) => setSelectedObra(e.target.value as number)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {obras.map(obra => (
                                    <MenuItem key={obra.id} value={obra.id}>{obra.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filtrar por Investidor</InputLabel>
                            <Select
                                value={selectedInvestidor}
                                label="Filtrar por Investidor"
                                onChange={(e) => setSelectedInvestidor(e.target.value as number)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {investidores.map(inv => (
                                    <MenuItem key={inv.id} value={inv.id}>{inv.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Data Inicial"
                            InputLabelProps={{ shrink: true }}
                            value={filterDataInicio}
                            onChange={(e) => setFilterDataInicio(e.target.value)}
                        />
                    </Box>
                    <Box>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Data Final"
                            InputLabelProps={{ shrink: true }}
                            value={filterDataFim}
                            onChange={(e) => setFilterDataFim(e.target.value)}
                        />
                    </Box>
                </Box>
            </Paper>

            {/* Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
                <Box>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Total Aportes</Typography>
                            <Typography variant="h5" color="primary">{formatCurrency(totalAportes)}</Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box>
                    <Card sx={{ bgcolor: '#ffebee' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Total Resgates</Typography>
                            <Typography variant="h5" color="error">{formatCurrency(totalResgates)}</Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Saldo Líquido</Typography>
                            <Typography variant="h5" sx={{ color: saldo >= 0 ? 'success.main' : 'error.main' }}>
                                {formatCurrency(saldo)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell>Data</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Obra</TableCell>
                                <TableCell>Investidor</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell>Observações</TableCell>
                                <TableCell align="center">Anexos</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {aportesResgates.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{formatDate(item.data)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.tipo}
                                            color={item.tipo === 'Aporte' ? 'success' : 'error'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{item.obra_nome}</TableCell>
                                    <TableCell>{item.investidor_nome}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: item.tipo === 'Aporte' ? 'success.main' : 'error.main' }}>
                                        {formatCurrency(item.valor)}
                                    </TableCell>
                                    <TableCell>{item.observacoes || '-'}</TableCell>
                                    <TableCell align="center">
                                        {item.anexos && item.anexos.length > 0 && (
                                            <IconButton size="small" onClick={(e) => handleOpenAnexos(e, item.anexos || [])}>
                                                <AttachFileIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenModal(item)}><EditIcon /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {aportesResgates.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">Nenhum registro encontrado.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Edit/Add Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Editar Lançamento' : 'Novo Lançamento'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={formData.tipo}
                                label="Tipo"
                                onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'Aporte' | 'Resgate' })}
                            >
                                <MenuItem value="Aporte">Aporte</MenuItem>
                                <MenuItem value="Resgate">Resgate</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Obra</InputLabel>
                            <Select
                                value={formData.obra_id || ''}
                                label="Obra"
                                onChange={(e) => setFormData({ ...formData, obra_id: Number(e.target.value) })}
                            >
                                {obras.map(o => <MenuItem key={o.id} value={o.id}>{o.nome}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Investidor</InputLabel>
                            <Select
                                value={formData.investidor_id || ''}
                                label="Investidor"
                                onChange={(e) => setFormData({ ...formData, investidor_id: Number(e.target.value) })}
                            >
                                {investidores.map(i => <MenuItem key={i.id} value={i.id}>{i.nome}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Valor"
                            type="number"
                            fullWidth
                            value={formData.valor || ''}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value ? Number(e.target.value) : ('' as any) })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            }}
                        />

                        <TextField
                            label="Data"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: "9999-12-31" }}
                            value={formData.data}
                            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                        />

                        <TextField
                            label="Observações"
                            multiline
                            rows={3}
                            fullWidth
                            value={formData.observacoes || ''}
                            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                        />

                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                sx={{ mb: 2 }}
                            >
                                Anexar Arquivos
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={handleFileChange}
                                />
                            </Button>

                            {/* Existing Attachments */}
                            {existingAnexos.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Anexos Existentes:</Typography>
                                    {existingAnexos.map((anexo) => (
                                        <Box key={anexo.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                            {getFileIcon(anexo.nome_original)}
                                            <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <a href={getAporteResgateAnexoUrl(anexo.caminho_arquivo)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                                    {anexo.nome_original}
                                                </a>
                                            </Typography>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveExistingAnexo(anexo.id)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* New Attachments */}
                            {anexos.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Novos Anexos:</Typography>
                                    {anexos.map((file, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                            {getFileIcon(file.name)}
                                            <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {file.name}
                                            </Typography>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveAnexo(index)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} disabled={uploading}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={uploading}>
                        {uploading ? <CircularProgress size={24} /> : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popover
                id={idPopover}
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleCloseAnexos}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <List sx={{ minWidth: 200 }}>
                    {viewingAnexos.map((anexo) => (
                        <ListItem key={anexo.id}>
                            <ListItemIcon>
                                {getFileIcon(anexo.nome_original)}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Link href={getAporteResgateAnexoUrl(anexo.caminho_arquivo)} target="_blank" rel="noopener noreferrer" underline="hover">
                                        {anexo.nome_original}
                                    </Link>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Popover>
        </Box>
    );
};

export default AportesResgatesPage;
